/**
 * Phone validation for lead capture (F-0058).
 *
 * ## Why this file exists at all
 *
 * The console repo owns the authoritative parser (`app/lib/phone/parsePhone.ts`) and this is a
 * deliberately minimal mirror of the part a form needs. The two repos deploy independently and
 * share no package, so the alternative is what was here before: `/^[6-9]\d{9}$/`, inline.
 *
 * ## What that regex cost
 *
 * It rejected numbers the backend handles correctly, and a rejection at the form means the
 * customer is turned away and **no lead is created** — invisible to us, because nothing is
 * logged when a form refuses to submit. Concretely it refused:
 *
 *  - a number typed the way it appears on the customer's own phone: `+91 91234 56789`;
 *  - `919123456789`, the form a WhatsApp contact share produces;
 *  - `09876543210`, the trunk-prefixed form people still write;
 *  - every non-Indian number, on a form that is now offered to customers abroad.
 *
 * Keep this file in step with the console's `validateForCountry`. It intentionally carries only
 * the countries the consumer app offers rather than the full table — an unrecognised code is
 * accepted on shape alone, so being incomplete costs a country label, never a lead.
 */

export interface CountryOption {
  code: string;
  country: string;
  flag: string;
  minDigits: number;
  maxDigits: number;
}

export const COUNTRY_OPTIONS: CountryOption[] = [
  { code: '+91', country: 'India', flag: '🇮🇳', minDigits: 10, maxDigits: 10 },
  { code: '+1', country: 'USA / Canada', flag: '🇺🇸', minDigits: 10, maxDigits: 10 },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', minDigits: 10, maxDigits: 10 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', minDigits: 9, maxDigits: 9 },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', minDigits: 8, maxDigits: 8 },
  { code: '+61', country: 'Australia', flag: '🇦🇺', minDigits: 9, maxDigits: 9 },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', minDigits: 9, maxDigits: 9 },
  { code: '+974', country: 'Qatar', flag: '🇶🇦', minDigits: 8, maxDigits: 8 },
  { code: '+968', country: 'Oman', flag: '🇴🇲', minDigits: 8, maxDigits: 8 },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', minDigits: 9, maxDigits: 10 },
  { code: '+49', country: 'Germany', flag: '🇩🇪', minDigits: 10, maxDigits: 11 },
  { code: '+353', country: 'Ireland', flag: '🇮🇪', minDigits: 9, maxDigits: 9 },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', minDigits: 8, maxDigits: 10 },
];

export const DEFAULT_COUNTRY = '+91';

/** E.164 allows at most 15 digits including the country code. */
const E164_MAX_DIGITS = 15;
/** Below this, a value is a short code or a typo rather than a phone number. */
const MIN_PLAUSIBLE_DIGITS = 7;

/**
 * One contiguous run of digits-and-separators, i.e. one candidate number in a
 * sentence. Kept identical to the run-splitting the console's chatbot engine
 * does, so "Rahul — 98765 43210" splits the same way in both repos.
 */
const PHONE_RUN_PATTERN = /[+\d][\d\s\-()]*\d/g;

export type PhoneResult =
  | { ok: true; localDigits: string; countryCode: string }
  | { ok: false; error: string };

function digitsOf(raw: string): string {
  return String(raw ?? '').replace(/\D/g, '');
}

export function getCountry(code: string): CountryOption | undefined {
  return COUNTRY_OPTIONS.find((c) => c.code === code);
}

/**
 * Validate a typed number against the selected country and return its LOCAL digits.
 *
 * The country the customer picked is treated as a fact and wins over anything inferred from
 * the digits — it is also what the server stores, so the label the customer saw is the label
 * their lead carries.
 */
export function validatePhone(
  raw: string,
  // Nullable on purpose. A JS default parameter fires only for `undefined`, so a caller
  // holding a NULL country code (`session.user.countryCode` is null for every customer
  // created before F-0057 recorded it) used to pass null straight through — and null then
  // fell into the "unknown code" branch below, which skips BOTH the length check and the
  // Indian 6-9 rule. That accepted a 12-digit Aadhaar number as a phone, the exact hole
  // the structured picker exists to close.
  //
  // So a MISSING country is normalised to the house market. An UNRECOGNISED one still
  // takes the shape-only branch below, by design (see the note at the top of this file) —
  // that is what keeps a real foreign lead from being rejected. Any caller that accepts a
  // code off the network therefore has to decide for itself whether to allowlist it; the
  // OTP send is what actually proves the number, so the widget deliberately doesn't.
  suppliedCountryCode: string | null | undefined = DEFAULT_COUNTRY
): PhoneResult {
  const countryCode = String(suppliedCountryCode ?? '').trim() || DEFAULT_COUNTRY;
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: false, error: 'Mobile number is required' };

  // A national trunk prefix (`09876543210`) is a formatting habit, not part of the number.
  let digits = digitsOf(trimmed).replace(/^0+/, '');
  if (!digits) return { ok: false, error: 'Enter a valid mobile number' };
  if (digits.length < MIN_PLAUSIBLE_DIGITS || digits.length > E164_MAX_DIGITS) {
    return { ok: false, error: 'Enter a valid mobile number' };
  }

  const country = getCountry(countryCode);
  if (!country) {
    // Unknown code: accept on E.164 shape alone rather than lose the lead.
    return { ok: true, localDigits: digits, countryCode };
  }

  // The value may already carry its own country code. Strip it only when doing so still
  // leaves a plausible local number — an Indian mobile can itself begin "91", and stripping
  // unconditionally turns `9123456789` into an 8-digit number belonging to nobody.
  const ccDigits = countryCode.slice(1);
  if (digits.startsWith(ccDigits) && digits.length > country.minDigits) {
    const stripped = digits.slice(ccDigits.length);
    if (stripped.length >= country.minDigits && stripped.length <= country.maxDigits) {
      digits = stripped;
    }
  }

  if (digits.length < country.minDigits || digits.length > country.maxDigits) {
    const expected =
      country.minDigits === country.maxDigits
        ? `${country.minDigits}`
        : `${country.minDigits}-${country.maxDigits}`;
    return { ok: false, error: `Enter a valid ${expected}-digit ${country.country} mobile number` };
  }

  // India-only: mobiles start 6-9. Not generalised — we do not carry other countries' prefix
  // rules, and guessing them would re-create the reject-a-valid-number bug elsewhere.
  if (countryCode === DEFAULT_COUNTRY && !/^[6-9]/.test(digits)) {
    return { ok: false, error: 'An Indian mobile number must start with 6, 7, 8 or 9' };
  }

  return { ok: true, localDigits: digits, countryCode };
}

/**
 * First run of free text that could be a phone number, as plain digits.
 *
 * Deliberately country-agnostic and deliberately NOT a validator: callers use it
 * to *notice* that a message carries a number (e.g. the chatbot's OTP gate must
 * hold back any number, not just Indian ones — an India-only detector would let
 * a foreign number slip past the very guard it exists to apply). Whether the
 * number is real is `validatePhone`'s job, once a country is known.
 *
 * Bounds are the same E.164 window `validatePhone` uses, so the two can't drift.
 */
export function findPhoneCandidate(text: string): string | null {
  for (const run of String(text ?? '').match(PHONE_RUN_PATTERN) ?? []) {
    const digits = digitsOf(run).replace(/^0+/, '');
    if (digits.length >= MIN_PLAUSIBLE_DIGITS && digits.length <= E164_MAX_DIGITS) {
      return digits;
    }
  }
  return null;
}

/** `text` with every candidate number removed — e.g. the name out of "Rahul — 98765 43210". */
export function stripPhoneNumbers(text: string): string {
  return String(text ?? '')
    .replace(PHONE_RUN_PATTERN, '')
    .replace(/[—–-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Do two numbers refer to the same line? Compares on digits so a stored local
 * number, an E.164 one and a country-code-prefixed one all match. Used to skip a
 * second OTP for a number the visitor has already proven.
 */
/**
 * Are these the same phone line? **Exact identity only.**
 *
 * This is used to decide whether a number has already been PROVEN by OTP, so a loose
 * match is an authorisation bypass, not a UX nicety. It previously compared with
 * `endsWith` in both directions to paper over the local-vs-E.164 mix, which meant:
 *
 *  - a 7-digit SUFFIX of a proven number counted as that number — so after verifying
 *    `+91 9876543210`, typing `Rahul 6543210 9123456789` skipped the gate entirely and
 *    the engine (which picks a different digit run) created a lead for a stranger;
 *  - `+1 9876543210` matched `+91 9876543210` — a US line "proven" by an Indian OTP.
 *
 * Both were reachable through the normal widget UI. So: build full E.164 on both sides
 * from a KNOWN dialling code and compare with `===`. A side whose country is unknown
 * is compared only after stripping a known code from the other — never by suffix — and
 * when neither side can be pinned down the answer is `false`. Refusing to decide costs
 * one extra OTP; guessing costs a lead filed against someone who never consented.
 */
export function isSamePhone(
  a: { countryCode?: string | null; phone?: string | null } | null,
  b: { countryCode?: string | null; phone?: string | null } | null
): boolean {
  const canonical = (
    v: { countryCode?: string | null; phone?: string | null } | null
  ): { e164: string | null; local: string } => {
    const local = digitsOf(v?.phone ?? '').replace(/^0+/, '');
    const code = getCountry(String(v?.countryCode ?? '').trim());
    return { e164: code && local ? `${digitsOf(code.code)}${local}` : null, local };
  };

  const x = canonical(a);
  const y = canonical(b);
  if (!x.local || !y.local) return false;

  // Both countries known → the only comparison that proves identity.
  if (x.e164 && y.e164) return x.e164 === y.e164;

  // One side is bare local digits. Accept it only as the exact local part of the
  // other's E.164 — i.e. the same subscriber number under the known country.
  if (x.e164 && !y.e164) return x.local === y.local;
  if (y.e164 && !x.e164) return y.local === x.local;

  // Neither side carries a country. Local digits alone cannot identify a line.
  return false;
}

